import { Bot, Factory, type LucideIcon, PackageCheck, TriangleAlert } from "lucide-react";
import type { FactoryLayout, FactoryState } from "../../types";
import { StatCell, StatStrip } from "../stat-strip";

interface Kpi {
  label: string;
  value: number;
  icon: LucideIcon;
  headline: string;
  detail: string;
  alert?: boolean;
  bad?: boolean;
}

export function KpiCards({ layout, state }: { layout: FactoryLayout; state: FactoryState }) {
  const { kpis } = state;
  const input = state.runs.filter((r) => r.status === "waiting_for_input").length;
  const attention = kpis.blocked + kpis.waitingForApproval + input;
  const stations = Object.values(state.stations);
  const busy = stations.filter((s) => s.active > 0).length;
  const hotfix = state.tasks.filter((t) => t.lane === "hotfix" && t.status === "open").length;
  const agentCount = layout.sections.reduce((n, s) => n + s.agents.length, 0);

  const items: Kpi[] = [
    {
      label: "On the line",
      value: kpis.inProduction,
      icon: Factory,
      headline: `${kpis.queued} queued for a station`,
      detail: hotfix ? `${hotfix} on the hotfix express` : "No hotfixes in flight",
    },
    {
      label: "Agents working",
      value: kpis.runningAgents,
      icon: Bot,
      headline: `${busy} of ${agentCount} stations busy`,
      detail: "Runs launching, running or awaiting input",
    },
    {
      label: "Needs attention",
      value: attention,
      icon: TriangleAlert,
      alert: attention > 0,
      headline: attention ? "Waiting on a human" : "Nothing waiting on you",
      detail: `${kpis.waitingForApproval} approvals · ${kpis.blocked} blocked · ${input} input`,
    },
    {
      label: "Shipped (24h)",
      value: kpis.completedLast24h,
      icon: PackageCheck,
      headline: `${kpis.mergedLast24h} pull requests merged`,
      detail: kpis.failedLast24h
        ? `${kpis.failedLast24h} failed in the last day`
        : "No failures in the last day",
      bad: kpis.failedLast24h > 0,
    },
  ];

  return (
    <StatStrip columns={4}>
      {items.map((item) => (
        <StatCell
          key={item.label}
          label={item.label}
          icon={item.icon}
          value={item.value}
          tone={item.alert ? "warn" : item.bad ? "bad" : "neutral"}
          detail={
            <>
              <span className="text-foreground/80">{item.headline}</span> · {item.detail}
            </>
          }
        />
      ))}
    </StatStrip>
  );
}
